import { Injectable } from "@angular/core";
import Clarity from "@microsoft/clarity";
import { GlobalsService } from "./utils/globals.service";

/**
 * Microsoft Clarity - heatmaps and session recordings, desktop only.
 *
 * Installed via the official @microsoft/clarity package rather than by pasting Clarity's snippet
 * into index.html. That matters for more than tidiness: the snippet loads on every page including
 * the login screen, for signed-out visitors, and before this service can apply masking. Starting
 * it from code means it only ever runs for a signed-in user, in a configured environment, after
 * the page is masked.
 *
 * Why only desktop: Clarity (like Hotjar) is built for websites, not native app webviews, so
 * running it inside Capacitor is unsupported territory. The mobile app reports module usage
 * through UsageService instead, which works everywhere and keeps the data ours.
 *
 * PRIVACY - the important part. JobSight's screens are full of our customers' customers' data:
 * names, addresses, phone numbers, job notes, invoice amounts, card last-four. Session recording
 * captures the DOM, so masking is not optional here.
 *
 * Clarity's masking mode lives in their web portal, which means it is not in source control and
 * anyone with portal access can weaken it. So the page also carries data-clarity-mask="True",
 * which the docs define as overriding the portal setting - masking survives a portal
 * misconfiguration rather than depending on one. It is applied before init() so there is no window
 * where content could be captured unmasked. Heatmaps are unaffected either way, because those are
 * click coordinates rather than content.
 *
 * If readable recordings are ever wanted, unmask individual non-sensitive elements with
 * data-clarity-unmask - never by relaxing the mode.
 */
@Injectable({ providedIn: "root" })
export class ClarityService {
	/**
	 * Clarity project ids. Desktop and mobile would be separate projects so the two apps report
	 * separately; only desktop is wired today.
	 *
	 * !! NOT YET CREATED !! Sign in at https://clarity.microsoft.com, create a project, and paste
	 * its id here. Until then this service does nothing at all, which is the intended default -
	 * no id means no init and no data leaves the browser.
	 */
	private static readonly productionProjectId: string = "xwombqkvsf";
	private static readonly stagingProjectId: string = "xwombqkvsf";

	private isStarted: boolean = false;

	/**
	 * Starts Clarity and identifies the session. Safe to call repeatedly - it initialises once.
	 *
	 * Called after login rather than at bootstrap so a session is attributable to a user and a
	 * company from its first event, and so nothing is recorded on the login screen.
	 */
	start() {
		if (this.isStarted)
			return;

		const projectId = this.getProjectId();

		// No project configured for this environment - including local dev, deliberately.
		if (!projectId)
			return;

		this.isStarted = true;

		try {
			// Before init(), always. See the class comment - this is the masking guarantee that
			// doesn't depend on the portal being configured correctly.
			document.documentElement.setAttribute("data-clarity-mask", "True");

			Clarity.init(projectId);

			this.identify();
		}
		catch {
			// Analytics must never break the app.
		}
	}

	private getProjectId(): string {
		if (GlobalsService.isDebug) {
			// Opt-in for verifying the integration locally - point it at the STAGING project, never
			// production, so test sessions don't pollute real data:
			//   localStorage.setItem("CLARITY_DEV_PROJECT_ID", "<staging project id>")
			// Kept in localStorage rather than in a source-controlled flag so it can't ship switched
			// on, and so nobody's machine starts recording without them choosing to.
			return localStorage.getItem("CLARITY_DEV_PROJECT_ID") || "";
		}

		if (GlobalsService.isStaging || GlobalsService.isRC || GlobalsService.isHotfix)
			return ClarityService.stagingProjectId;

		return ClarityService.productionProjectId;
	}

	/**
	 * Attaches the user and company to the session so a recording can be traced back to whoever hit
	 * the problem. Ids only - no names or email addresses, which would defeat the masking.
	 */
	private identify() {
		const userId = GlobalsService.userInfo?.userId;
		const companyId = GlobalsService.companyInfo?.companyId;

		if (userId)
			Clarity.identify(userId.toString());

		if (companyId)
			Clarity.setTag("companyId", companyId.toString());

		Clarity.setTag("appVersion", GlobalsService.appVersion);
	}
}
